import { useCallback, useEffect, useState } from 'react'
import type { SessionInfo, WebAutomation } from '@shared/web'
import { AUTH_EXPIRED_EVENT, getSession, signOut } from './lib/api'
import { forgetGoogleSession } from './lib/google'
import SignInPage from './pages/SignInPage'
import HomePage from './pages/HomePage'
import AutomationPage from './pages/AutomationPage'

type Phase = 'loading' | 'signed-out' | 'signed-in'

export default function App(): JSX.Element {
  const [phase, setPhase] = useState<Phase>('loading')
  const [session, setSession] = useState<SessionInfo | null>(null)
  const [selected, setSelected] = useState<WebAutomation | null>(null)
  const [authNotice, setAuthNotice] = useState('')

  const refreshSession = useCallback(async () => {
    try {
      setSession(await getSession())
      setPhase('signed-in')
    } catch {
      setSession(null)
      setPhase('signed-out')
    }
  }, [])

  useEffect(() => {
    void refreshSession()
    // Any call failing with an expired session routes back to sign-in with an
    // explanation, instead of stranding the user among dead buttons.
    const onExpired = (): void => {
      setAuthNotice('Your session expired. Please sign in again to continue.')
      setSelected(null)
      setSession(null)
      setPhase('signed-out')
    }
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired)
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired)
  }, [refreshSession])

  if (phase === 'loading') {
    return <div className="center-note">Loading…</div>
  }

  if (phase === 'signed-out') {
    return (
      <SignInPage
        notice={authNotice}
        onSignedIn={() => {
          setAuthNotice('')
          return refreshSession()
        }}
      />
    )
  }

  return (
    <div className="shell">
      <header className="topbar">
        <button className="brand-btn" onClick={() => setSelected(null)} title="All automations">
          Autom<span>8</span>
        </button>
        <div className="topbar-right">
          {/* Whose automations these are. Owners belong to no company, so they
              see only their address. */}
          {session?.company && <span className="account">{session.company}</span>}
          {session?.email && <span className="account">{session.email}</span>}
          {/* A new tab, not a route: the guide is a static document served by
              the broker, and someone reading it halfway through sending files
              should not lose the page they were on. */}
          <a className="btn subtle" href="/guide" target="_blank" rel="noopener">
            Guide
          </a>
          <button
            className="btn subtle"
            onClick={() => {
              void signOut()
                .catch(() => {})
                .then(() => {
                  // Also clear GIS's own session hint, or the next visit
                  // silently signs the same person back in.
                  forgetGoogleSession()
                  setSelected(null)
                  setSession(null)
                  setAuthNotice('')
                  setPhase('signed-out')
                })
            }}
          >
            Sign out
          </button>
        </div>
      </header>
      <main className="content">
        {selected ? (
          <AutomationPage automation={selected} onBack={() => setSelected(null)} />
        ) : (
          <HomePage onOpen={setSelected} />
        )}
      </main>
    </div>
  )
}
